/**
 * Quick Filters Component
 *
 * Compact toolbar for sorting and filtering tasks.
 * Designed to sit alongside or below the view tabs.
 */

import { ArrowUpDown, Check, ChevronDown, Search, X, Mic, Sparkles } from 'lucide-react';
import { useState, useRef, useEffect, type ReactNode } from 'react';
import clsx from 'clsx';

export type SortOption = 'due' | 'priority' | 'created' | 'title';
export type SortDirection = 'asc' | 'desc';

export interface QuickFiltersProps {
  sortBy: SortOption;
  sortDirection: SortDirection;
  showCompleted: boolean;
  searchTerm: string;
  onSortChange: (sortBy: SortOption, direction: SortDirection) => void;
  onShowCompletedChange: (show: boolean) => void;
  onSearchChange: (term: string) => void;
  /** When true, the mobile filter panel is shown (controlled externally) */
  mobileFiltersOpen?: boolean;
  onMobileFiltersClose?: () => void;
  /** Optional callback to open Brain Dump flow (desktop only) */
  onBrainDump?: () => void;
  /** Whether semantic search is currently active */
  isSemanticActive?: boolean;
  /** The search mode returned from the backend */
  searchMode?: 'semantic' | 'keyword' | null;
  /** Optional slot rendered at the right end of the desktop toolbar (e.g. Chat toggle) */
  rightSlot?: ReactNode;
}

const sortOptions: { value: SortOption; label: string }[] = [
  { value: 'due', label: 'Due Date' },
  { value: 'priority', label: 'Priority' },
  { value: 'created', label: 'Created' },
  { value: 'title', label: 'Title' },
];

export function QuickFilters({
  sortBy,
  sortDirection,
  showCompleted,
  searchTerm,
  onSortChange,
  onShowCompletedChange,
  onSearchChange,
  mobileFiltersOpen,
  onMobileFiltersClose,
  onBrainDump,
  isSemanticActive = false,
  searchMode = null,
  rightSlot,
}: QuickFiltersProps) {
  const [isSortOpen, setIsSortOpen] = useState(false);
  const filtersOpen = mobileFiltersOpen ?? false;
  const sortRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setIsSortOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentSortLabel = sortOptions.find((o) => o.value === sortBy)?.label || 'Sort';

  const handleSortSelect = (option: SortOption) => {
    // If clicking the same option, toggle direction
    if (option === sortBy) {
      onSortChange(option, sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New option, default to ascending for priority (1=urgent is highest), ascending for others
      const defaultDirection = option === 'priority' ? 'asc' : 'asc';
      onSortChange(option, defaultDirection);
    }
    setIsSortOpen(false);
  };

  // hasActiveFilters used by desktop indicator — keep for future use
  const _hasActiveFilters = showCompleted || searchTerm || sortBy !== 'due';
  void _hasActiveFilters;

  // Shared sort dropdown content
  const sortDropdown = (
    <div className="relative" ref={sortRef}>
      <button
        onClick={() => setIsSortOpen(!isSortOpen)}
        className={clsx(
          'flex items-center gap-1 px-3 py-2 md:px-2 md:py-0.5 rounded text-sm md:text-xs transition-colors cursor-pointer whitespace-nowrap shrink-0',
          'text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark'
        )}
      >
        <ArrowUpDown className="w-4 h-4 md:w-3 md:h-3" />
        <span>{currentSortLabel}</span>
        <span className="text-text-tertiary-light dark:text-text-secondary-dark">{sortDirection === 'asc' ? '↓' : '↑'}</span>
        <ChevronDown className={clsx('w-4 h-4 md:w-3 md:h-3 text-text-tertiary-light dark:text-text-secondary-dark transition-transform', isSortOpen && 'rotate-180')} />
      </button>

      {isSortOpen && (
        <div className="absolute top-full left-0 mt-1 bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-md shadow-lg z-50 min-w-[120px] py-1 animate-fade-in">
          {sortOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handleSortSelect(option.value)}
              className={clsx(
                'w-full flex items-center justify-between gap-2 px-3 py-2.5 md:py-1.5 text-left text-xs transition-colors cursor-pointer',
                sortBy === option.value
                  ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                  : 'text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark'
              )}
            >
              <span>{option.label}</span>
              {sortBy === option.value && (
                <span className="text-primary-500 dark:text-primary-400 text-xs md:text-[10px]">
                  {sortDirection === 'asc' ? '↓' : '↑'}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // Shared completed toggle
  const completedToggle = (
    <button
      onClick={() => onShowCompletedChange(!showCompleted)}
      className={clsx(
        'flex items-center gap-1 px-2 py-2 md:py-0.5 rounded text-xs transition-colors cursor-pointer',
        showCompleted
          ? 'text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/30'
          : 'text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark'
      )}
    >
      <div className={clsx(
        'w-4 h-4 md:w-3 md:h-3 rounded border flex items-center justify-center transition-colors',
        showCompleted
          ? 'bg-primary-500 border-primary-500'
          : 'border-border-hover-light dark:border-border-hover-dark bg-surface-raised-light dark:bg-surface-raised-dark'
      )}>
        {showCompleted && <Check className="w-2 h-2 text-white" />}
      </div>
      <span>Done</span>
    </button>
  );

  // Shared search input
  const searchInput = (mobile: boolean) => (
    <div className={clsx('relative flex items-center', mobile && 'w-full')}>
      <Search className={clsx('absolute left-2 text-text-tertiary-light dark:text-text-secondary-dark pointer-events-none', mobile ? 'w-4 h-4' : 'w-3 h-3')} />
      <input
        ref={mobile ? undefined : searchInputRef}
        type="text"
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search..."
        className={clsx(
          'rounded transition-all',
          mobile
            ? 'w-full py-2.5 text-base bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark'
            : 'w-32 py-0.5 text-xs bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-hover-dark',
          'text-text-secondary-light dark:text-text-primary-dark placeholder-text-tertiary-light dark:placeholder-text-secondary-dark',
          'focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500',
          // Adjust padding based on whether semantic badge is visible
          isSemanticActive && searchMode === 'semantic'
            ? mobile ? 'pl-8 pr-24' : 'pl-8 pr-20'
            : 'pl-8 pr-8',
          !mobile && searchTerm && 'w-40'
        )}
      />
      {/* Semantic search badge — shown inside the input when AI search is active */}
      {isSemanticActive && searchMode === 'semantic' && (
        <span className={clsx(
          'absolute flex items-center gap-0.5 pointer-events-none',
          'text-amber-600 dark:text-amber-400 font-medium',
          mobile ? 'right-8 text-xs' : 'right-6 text-[10px]'
        )}>
          <span>✦</span>
          <span>Smart</span>
        </span>
      )}
      {searchTerm && (
        <button
          onClick={() => onSearchChange('')}
          className={clsx('absolute right-1.5 rounded hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors', mobile ? 'p-2' : 'p-0.5')}
        >
          <X className={clsx('text-text-tertiary-light dark:text-text-secondary-dark', mobile ? 'w-4 h-4' : 'w-3 h-3')} />
        </button>
      )}
    </div>
  );

  return (
    <div>
      {/* Mobile: filter panel (controlled externally from dots menu) */}
      {filtersOpen && (
        <div className="md:hidden fixed inset-0 z-50" onClick={onMobileFiltersClose}>
          <div className="absolute inset-0 bg-black/20" />
          <div
            className="absolute right-3 w-64 bg-surface-raised-light/95 dark:bg-surface-raised-dark/95 backdrop-blur-md rounded-xl border border-border-subtle-light dark:border-border-subtle-dark shadow-lg p-3 space-y-3"
            style={{ top: 'calc(3.5rem + env(safe-area-inset-top, 0px))' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2">
              {sortDropdown}
              <div className="w-px h-4 bg-border-subtle-light dark:bg-border-subtle-dark" />
              {completedToggle}
            </div>
            {searchInput(true)}
          </div>
        </div>
      )}

      {/* Desktop: existing horizontal bar (unchanged) */}
      <div className="hidden md:flex items-center gap-2 px-3 py-1 bg-surface-base-light dark:bg-surface-raised-dark border-y border-border-subtle-light dark:border-border-subtle-dark">
        {sortDropdown}
        <div className="w-px h-4 bg-border-subtle-light dark:bg-border-subtle-dark" />
        {completedToggle}
        <div className="w-px h-4 bg-border-subtle-light dark:bg-border-subtle-dark" />
        {searchInput(false)}
        {(onBrainDump || rightSlot) && (
          <>
            <div className="flex-1" />
            {onBrainDump && (
              <button
                onClick={onBrainDump}
                className="flex items-center gap-1 px-2 py-0.5 rounded text-xs text-text-secondary-light dark:text-text-secondary-dark hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors whitespace-nowrap shrink-0"
                title="Brain Dump — speak multiple tasks at once"
              >
                <Mic className="w-3 h-3" />
                <Sparkles className="w-3 h-3" />
                <span className="hidden xl:inline">Brain Dump</span>
              </button>
            )}
            {rightSlot && (
              <div className="flex items-center pl-2">
                {rightSlot}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
